import React, { useEffect, useState } from 'react';
import { BiUserCircle } from 'react-icons/bi';
import GenericNavBar from 'newComponents/GenericNavBar';
import facebookIcon from 'assets/icons/facebook.svg';
import instagramIcon from 'assets/icons/instragam.svg';
import whatsappIcon from 'assets/icons/whatsapp.svg';
import youtubeIcon from 'assets/icons/youtube.svg';
import headsetIcon from 'assets/icons/new-headset.svg';
import {
  ConsultantDataReponse,
  getConsultants,
} from 'services/consultantsServices';
import phoneMask from 'utils/phoneMask';
import { buildTelHref, buildWhatsAppHref } from 'utils/buildContactLinks';
import { useTour } from 'hooks/useTour';
import TourBalloon from 'newComponents/NewTour/TourBallon';
import { ContactEmailIcon, ContactPhoneIcons } from './ContactActionIcons';
import ContactSectionInfo from './ContactSectionInfo';
import {
  DEFAULT_RCR_WHATSAPP_PHONE,
  RCR_CONTACT_PHONES,
} from './contactsConfig';
import {
  CONSULTANT_INTRO,
  RCR_INTRO,
  WHEN_TO_CONTACT_CONSULTANT_TITLE,
  WHEN_TO_CONTACT_RCR_TITLE,
  WHEN_TO_CONTACT_TOPICS,
} from './contactsContent';
import {
  Card,
  CardContent,
  Columns,
  ContactRow,
  Container,
  DescriptionContainer,
  DescriptionTitle,
  DetailTitle,
  EmailLink,
  HeadsetIcon,
  Hint,
  IconCircle,
  Line,
  Muted,
  PageIntroBlock,
  PhoneLink,
  Rule,
  SectionCol,
  SocialFollowRow,
  SocialIconsGroup,
  SocialMediaIconLink,
  SocialMediasTitle,
  SocialMediaText,
  Spinner,
  SpinnerWrap,
  Stack,
  TableWrap,
  WhatsAppButton,
  WhatsAppOnlyContent,
} from './styles';

function fixPhoto(s: string): string {
  const t = s.trim();
  if (!t) return '';
  const j = t.indexOf('base64,');
  if (j !== -1) return t.slice(0, j + 7) + t.slice(j + 7).replace(/\s/g, '');
  return t.replace(/\s/g, '');
}

function isRcrAttendant(consultantName: string): boolean {
  return consultantName
    .toLocaleLowerCase()
    .includes('atendimento comercial');
}

function RcrContact() {
  return (
    <ContactRow>
      <IconCircle>
        <HeadsetIcon src={headsetIcon} alt="" aria-hidden />
      </IconCircle>
      <Stack>
        <DetailTitle>Fale conosco</DetailTitle>
        {RCR_CONTACT_PHONES.map((contact) => (
          <Line key={contact.tel}>
            <span>
              <PhoneLink href={buildTelHref(contact.tel)}>{contact.display}</PhoneLink>{' '}
              {contact.hint && <Hint>{contact.hint}</Hint>}
            </span>
            <ContactPhoneIcons
              phone={contact.tel}
              whatsappPhone={contact.whatsappPhone}
              phoneLabel={contact.display}
            />
          </Line>
        ))}
      </Stack>
    </ContactRow>
  );
}

const avatarStyle: React.CSSProperties = {
  width: 64,
  height: 64,
  borderRadius: '50%',
  objectFit: 'cover',
  display: 'block',
  flexShrink: 0,
  backgroundColor: '#e5e7ff',
};

type ConsultantView = ConsultantDataReponse & {
  phoneRaw: string;
};

type ConsultantAvatarProps = {
  consultant: ConsultantView;
  imgBroken: boolean;
  onImgError: () => void;
};

function ConsultantAvatar({
  consultant,
  imgBroken,
  onImgError,
}: ConsultantAvatarProps) {
  if (consultant.photo && !imgBroken) {
    return (
      <img
        src={consultant.photo}
        alt={consultant.name || 'Consultor'}
        width={64}
        height={64}
        decoding="async"
        style={avatarStyle}
        onError={onImgError}
      />
    );
  }

  return (
    <IconCircle>
      <BiUserCircle size={42} color="#0B29FB" />
    </IconCircle>
  );
}

type ConsultantContactDetailsProps = {
  consultant: ConsultantView;
  imgBroken: boolean;
  onImgError: () => void;
};

function ConsultantContactDetails({
  consultant,
  imgBroken,
  onImgError,
}: ConsultantContactDetailsProps) {
  return (
    <ContactRow>
      <ConsultantAvatar
        consultant={consultant}
        imgBroken={imgBroken}
        onImgError={onImgError}
      />
      <Stack>
        <DetailTitle>{consultant.name || ''}</DetailTitle>
        {!!consultant.phone && (
          <Line>
            <PhoneLink href={buildTelHref(consultant.phoneRaw)}>
              <Muted>{consultant.phone}</Muted>
            </PhoneLink>
            <ContactPhoneIcons
              phone={consultant.phoneRaw}
              phoneLabel={consultant.name || 'Consultor comercial'}
            />
          </Line>
        )}
        {!!consultant.email && (
          <Line>
            <EmailLink href={`mailto:${consultant.email}`}>
              {consultant.email}
            </EmailLink>
            <ContactEmailIcon
              email={consultant.email}
              emailLabel={`Enviar e-mail para ${consultant.name || 'consultor'}`}
            />
          </Line>
        )}
      </Stack>
    </ContactRow>
  );
}

const BusinessContact: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [hasConsultant, setHasConsultant] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const [consultant, setConsultant] = useState<ConsultantView>({
    name: '',
    email: '',
    phone: '',
    phoneRaw: '',
    consultantName: '',
    photo: '',
  });

  const { showTour, tourType, tourStep, setTourType, setSteps } = useTour();

  const isRcrContact = isRcrAttendant(consultant.consultantName);
  const hasContactDetails = !!(
    consultant.phone?.trim() || consultant.email?.trim()
  );
  const showRcrLayout = hasConsultant && isRcrContact;
  const showFullLayout =
    hasConsultant && hasContactDetails && !isRcrContact;
  const showTwoColumnLayout = showFullLayout || showRcrLayout;
  const showWhatsAppLayout =
    hasConsultant && !hasContactDetails && !isRcrContact;
  const whatsappPhone = showWhatsAppLayout
    ? consultant.phoneRaw || DEFAULT_RCR_WHATSAPP_PHONE
    : DEFAULT_RCR_WHATSAPP_PHONE;
  const whatsappHref = buildWhatsAppHref(whatsappPhone);

  useEffect(() => {
    setTourType('businessContact');
    setSteps(1);
  }, [showTour, setTourType, setSteps]);

  useEffect(() => {
    setImgBroken(false);
  }, [consultant.photo]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const rows = await getConsultants();
      setLoading(false);

      if (!rows.length) {
        setHasConsultant(false);
        return;
      }

      const c = rows[0];
      const raw = c.photo;
      const photo =
        typeof raw === 'string' && raw.trim() ? fixPhoto(raw) : '';
      const phoneRaw = c.phone ?? '';

      setConsultant({
        name: c.name ?? '',
        email: (c.email ?? '').replace('.invalid', ''),
        phone: phoneRaw ? phoneMask(phoneRaw) : '',
        phoneRaw,
        consultantName: c.consultantName ?? '',
        photo,
      });
      setHasConsultant(true);
    }
    load();
  }, []);

  const tourActive =
    showTour && tourType === 'businessContact' && tourStep === 1;

  return (
    <GenericNavBar
      breadcrump="› Atendimento comercial"
      selected="atendimento-comercial"
      openSubMenu="contato"
      intro={() => true}
    >
      <Container>
        <DescriptionContainer>
          {loading && (
            <SpinnerWrap>
              <Spinner color="#0000ff" size={24} />
            </SpinnerWrap>
          )}
          {!loading && (
            <>
              <DescriptionTitle>Atendimento comercial</DescriptionTitle>
              <PageIntroBlock>
                <ContactSectionInfo
                  intro={showFullLayout ? CONSULTANT_INTRO : RCR_INTRO}
                  whenToContactTitle={
                    showFullLayout
                      ? WHEN_TO_CONTACT_CONSULTANT_TITLE
                      : WHEN_TO_CONTACT_RCR_TITLE
                  }
                  topics={WHEN_TO_CONTACT_TOPICS}
                />
              </PageIntroBlock>
            </>
          )}
        </DescriptionContainer>
        <Card>
          {loading && (
            <SpinnerWrap>
              <Spinner color="#0000ff" size={24} />
            </SpinnerWrap>
          )}
          {!loading && (
            <TableWrap showTour={tourActive}>
              <CardContent>
                {showTwoColumnLayout ? (
                  <Columns>
                    <SectionCol>
                      <ConsultantContactDetails
                        consultant={consultant}
                        imgBroken={imgBroken}
                        onImgError={() => setImgBroken(true)}
                      />
                    </SectionCol>
                    <Rule />
                    <SectionCol>
                      <RcrContact />
                    </SectionCol>
                  </Columns>
                ) : showWhatsAppLayout ? (
                  <WhatsAppOnlyContent>
                    <ContactRow>
                      <ConsultantAvatar
                        consultant={consultant}
                        imgBroken={imgBroken}
                        onImgError={() => setImgBroken(true)}
                      />
                      <Stack>
                        {!!consultant.name && (
                          <DetailTitle>{consultant.name}</DetailTitle>
                        )}
                        {whatsappHref && (
                          <WhatsAppButton
                            href={whatsappHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Entre em contato via whatsapp"
                          >
                            Entre em contato via whatsapp
                            <img src={whatsappIcon} alt="" aria-hidden />
                          </WhatsAppButton>
                        )}
                      </Stack>
                    </ContactRow>
                  </WhatsAppOnlyContent>
                ) : (
                  <SectionCol $full>
                    <RcrContact />
                  </SectionCol>
                )}
              </CardContent>
              {tourActive && (
                <TourBalloon
                  title="Atendimento Comercial"
                  content="Aqui você encontra os dados do atendimento comercial responsável pelo seu contrato."
                  position={{
                    left: '232px',
                    right: '0',
                    top: '0',
                    arrowPosition: 'left',
                    maxWidth: 'unset',
                    boxShadow: false,
                    width: '262px',
                    transform: 'translateX(100%)',
                  }}
                />
              )}
            </TableWrap>
          )}
          <SocialMediasTitle>
            <SocialMediaText>
              Para atendimento, dê preferência aos nossos canais digitais
            </SocialMediaText>
          </SocialMediasTitle>
          <SocialFollowRow>
            <SocialMediaText>Acompanhe nossas redes sociais</SocialMediaText>
            <SocialIconsGroup>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.instagram.com/ultragazoficial"
              >
                <img src={instagramIcon} alt="Instagram Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.facebook.com/ultragazoficial"
              >
                <img src={facebookIcon} alt="Facebook Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://api.whatsapp.com/send?phone=558007010123&data=AWCy0nzRCL3VWxyEoEkMLrTAIB8E1SVaU_fnKplhIRLu6d8NKzNq1mpB5QM8vC7DLKzXS4povPn2qJCCO0rmt2G4M_idEbmRWSrkozOarahtTdKFti50bPeZlnqyo6qFDdIgUt36PdbDCpHW7y_Yx5t6j9CpQ9w1fZQyqMEqsm1bRTyksYB2nF6_i8cLE458cowVRse0try5QcxGm29e-HrLvth6ql5sKO_PkUnKika8e4fffYsHjiP0e4a1NcIE_NGA_rjUGh0iGd1O0wqH6HmmuqDzeya_3IJOXb36&source=FB_Page&app=facebook&entry_point=page_cta"
              >
                <img src={whatsappIcon} alt="WhatsApp Ultragaz" />
              </SocialMediaIconLink>
              <SocialMediaIconLink
                target="_blank"
                rel="noopener noreferrer"
                href="https://www.youtube.com/channel/UCIJ0JnsSqv6LisxSgMsSuqg"
              >
                <img src={youtubeIcon} alt="YouTube Ultragaz" />
              </SocialMediaIconLink>
            </SocialIconsGroup>
          </SocialFollowRow>
        </Card>
      </Container>
    </GenericNavBar>
  );
};

export default BusinessContact;
